import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography
} from '@mui/material'
import { ArrowBack, Paid, Person, ReceiptLong, Star } from '@mui/icons-material'
import type { WarzoneCoinsExtrato } from '../../types'
import { apiService } from '../../services/api'

type UsuarioCompletoData = {
  usuario?: {
    id?: string
    created_at?: string
    updated_at?: string
    email?: string
    discord_id?: string
    steamid?: string
    enabled?: boolean
    active?: boolean
    whatsappNumber?: string
    acceptsNotification?: boolean
    roles?: string[]
    palworld?: boolean
    pal_server_name?: string | null
    pal_user_id?: string | null
    pal_player_id?: string | null
    pal_player_name?: string | null
  }
  perfil?: {
    email?: string
    whatsappNumber?: string
    palPlayerName?: string
    totalGastoPalworld?: number
    vipAtual?: {
      dataInicio?: string
      dataFim?: string
      diasVip?: number
      diasRestantes?: number
    } | null
  }
  pagamentosPalworld?: any[]
  vipPalworldHistorico?: any[]
  supplies?: any[]
  itemGrants?: any[]
  warzoneCoinExtrato?: WarzoneCoinsExtrato
}

type TabKey = 'pagamentos' | 'coins' | 'vip' | 'supplies' | 'grants'

const fmtDateTime = (iso?: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pt-BR')
}

const fmtMoney = (v: unknown) => {
  const n = typeof v === 'number' ? v : Number(v)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const boolChip = (v: boolean | undefined) => {
  if (v === undefined) return <Chip size="small" label="—" variant="outlined" />
  return <Chip size="small" label={v ? 'Sim' : 'Não'} color={v ? 'success' : 'default'} />
}

const statusChip = (v: string | undefined) => {
  if (!v) return <Chip size="small" label="—" variant="outlined" />
  const key = String(v).toUpperCase()
  const color =
    key === 'APROVADO' ? 'success' :
      key === 'PENDENTE' ? 'warning' :
        key === 'CANCELADO' || key === 'REJEITADO' || key === 'EXPIRADO' ? 'error' :
          'default'
  return <Chip size="small" label={v} color={color as any} />
}

const historicoTipoChip = (tipo: string | undefined) => {
  if (!tipo) return <Chip size="small" label="—" variant="outlined" />
  const t = String(tipo).toUpperCase()
  if (t === 'ENTRADA') return <Chip size="small" label="ENTRADA" color="success" variant="outlined" />
  if (t === 'SAIDA') return <Chip size="small" label="SAÍDA" color="warning" variant="outlined" />
  return <Chip size="small" label={tipo} variant="outlined" />
}

const toArray = <T,>(v: any): T[] => (Array.isArray(v) ? (v as T[]) : [])

const sortByCreatedAtDesc = (items: any[]) => {
  return [...items].sort((a, b) => {
    const ta = new Date(a?.createdAt ?? a?.created_at ?? 0).getTime()
    const tb = new Date(b?.createdAt ?? b?.created_at ?? 0).getTime()
    return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0)
  })
}

const slicePage = <T,>(items: T[], page: number, size: number) => items.slice(page * size, page * size + size)

const SectionCard: React.FC<{ title: string; subtitle?: string; icon?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  subtitle,
  icon,
  children
}) => {
  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {icon}
            <Typography variant="h6" sx={{ fontWeight: 950 }}>
              {title}
            </Typography>
          </Stack>
        }
        subheader={subtitle ? <Typography variant="body2" sx={{ color: 'text.secondary' }}>{subtitle}</Typography> : null}
        sx={{ pb: 0.5 }}
      />
      <CardContent sx={{ pt: 2.25 }}>{children}</CardContent>
    </Card>
  )
}

const AdminUsuarioCompletoPage: React.FC = () => {
  const { userId } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<UsuarioCompletoData | null>(null)
  const [tab, setTab] = useState<TabKey>('pagamentos')

  const [pagamentosPage, setPagamentosPage] = useState(0)
  const [pagamentosSize, setPagamentosSize] = useState(10)
  const [coinsPage, setCoinsPage] = useState(0)
  const [coinsSize, setCoinsSize] = useState(15)
  const [vipHistPage, setVipHistPage] = useState(0)
  const [vipHistSize, setVipHistSize] = useState(10)
  const [suppliesPage, setSuppliesPage] = useState(0)
  const [suppliesSize, setSuppliesSize] = useState(10)
  const [grantsPage, setGrantsPage] = useState(0)
  const [grantsSize, setGrantsSize] = useState(10)

  const load = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    setError(null)
    try {
      const r = await apiService.obterUsuarioPalworldCompletoAdmin({ userId, extratoPage: 0, extratoSize: 200 })
      setData(r as UsuarioCompletoData)
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar detalhes do usuário.')
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void load()
  }, [load])

  const title = useMemo(() => {
    const player = data?.usuario?.pal_player_name || data?.perfil?.palPlayerName
    const email = data?.usuario?.email || data?.perfil?.email
    return player || email || userId || 'Usuário'
  }, [data, userId])

  const perfil = data?.perfil
  const u = data?.usuario
  const extrato = data?.warzoneCoinExtrato

  const pagamentos = useMemo(() => sortByCreatedAtDesc(toArray<any>(data?.pagamentosPalworld)), [data])
  const vipHistorico = useMemo(() => sortByCreatedAtDesc(toArray<any>(data?.vipPalworldHistorico)), [data])
  const supplies = useMemo(() => toArray<any>(data?.supplies), [data])
  const grants = useMemo(() => sortByCreatedAtDesc(toArray<any>(data?.itemGrants)), [data])
  const coinsItems = useMemo(() => toArray<any>(extrato?.historico?.content), [extrato])

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person />
              {title}
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Detalhes completos do usuário (Palworld).
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/admin/usuarios')}>
              Voltar
            </Button>
            <Button variant="contained" onClick={load} disabled={loading}>
              Atualizar
            </Button>
          </Stack>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2.25}>
              <Grid item xs={12} md={4}>
                <SectionCard title="Identificação" subtitle="Dados principais do usuário." icon={<Person sx={{ opacity: 0.85 }} />}>
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Email
                      </Typography>
                      <Typography sx={{ fontWeight: 950, wordBreak: 'break-word' }}>{u?.email || '—'}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        ID: {u?.id || userId || '—'}
                      </Typography>
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                    <Grid container spacing={1.25}>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Player name
                        </Typography>
                        <Typography sx={{ fontWeight: 900 }}>{u?.pal_player_name || perfil?.palPlayerName || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          WhatsApp
                        </Typography>
                        <Typography sx={{ fontWeight: 800 }}>{u?.whatsappNumber || perfil?.whatsappNumber || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Discord
                        </Typography>
                        <Typography sx={{ fontWeight: 800 }}>{u?.discord_id || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          SteamID
                        </Typography>
                        <Typography sx={{ fontWeight: 800, wordBreak: 'break-word' }}>{u?.steamid || '—'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Criado em
                        </Typography>
                        <Typography sx={{ fontWeight: 800 }}>{fmtDateTime(u?.created_at)}</Typography>
                      </Grid>
                    </Grid>
                  </Stack>
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={4}>
                <SectionCard title="VIP e Palworld" subtitle="Resumo de VIP e compras." icon={<Star sx={{ opacity: 0.85 }} />}>
                  <Grid container spacing={1.25}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Dias restantes
                      </Typography>
                      <Typography sx={{ fontWeight: 950, fontSize: 28, lineHeight: 1.15 }}>
                        {perfil?.vipAtual?.diasRestantes ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Total gasto
                      </Typography>
                      <Typography sx={{ fontWeight: 950, fontSize: 28, lineHeight: 1.15 }}>
                        {fmtMoney(perfil?.totalGastoPalworld)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 0.75, borderColor: 'rgba(255,255,255,0.08)' }} />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Início
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }}>{fmtDateTime(perfil?.vipAtual?.dataInicio)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Fim
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }}>{fmtDateTime(perfil?.vipAtual?.dataFim)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Dias VIP
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }}>{perfil?.vipAtual?.diasVip ?? '—'}</Typography>
                    </Grid>
                  </Grid>
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={4}>
                <SectionCard title="Warzone Coins" subtitle="Saldo e movimentações." icon={<Paid sx={{ opacity: 0.85 }} />}>
                  <Grid container spacing={1.25}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Saldo
                      </Typography>
                      <Typography sx={{ fontWeight: 950, fontSize: 28, lineHeight: 1.15 }}>
                        {extrato?.saldo?.toLocaleString('pt-BR') ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Total gasto (coins)
                      </Typography>
                      <Typography sx={{ fontWeight: 950, fontSize: 28, lineHeight: 1.15 }}>
                        {extrato?.totalGasto?.toLocaleString('pt-BR') ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Total adicionado
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }}>{extrato?.totalAdicionado?.toLocaleString('pt-BR') ?? '—'}</Typography>
                    </Grid>
                  </Grid>
                </SectionCard>
              </Grid>
            </Grid>

            <Card>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Tabs
                  value={tab}
                  onChange={(_e, v) => setTab(v as TabKey)}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  sx={{ mb: 2 }}
                >
                  <Tab value="pagamentos" label={`Pagamentos (${pagamentos.length})`} />
                  <Tab value="coins" label={`Extrato Coins (${coinsItems.length})`} />
                  <Tab value="vip" label={`Histórico VIP (${vipHistorico.length})`} />
                  <Tab value="supplies" label={`Itens comprados (${supplies.length})`} />
                  <Tab value="grants" label={`Itens concedidos (${grants.length})`} />
                </Tabs>

                {tab === 'pagamentos' && (
                  <SectionCard
                    title="Pagamentos Palworld"
                    subtitle="Sem TXID. Mostra data, status, tipo e valor."
                    icon={<ReceiptLong sx={{ opacity: 0.85 }} />}
                  >
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {slicePage(pagamentos, pagamentosPage, pagamentosSize).map((p: any, idx: number) => (
                            <TableRow key={p?.id ?? `${p?.createdAt ?? 'row'}-${idx}`}>
                              <TableCell>{fmtDateTime(p?.createdAt)}</TableCell>
                              <TableCell>{statusChip(p?.status)}</TableCell>
                              <TableCell>{p?.tipoPagamento ?? '—'}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                {fmtMoney(p?.valorTotal)}
                              </TableCell>
                            </TableRow>
                          ))}

                          {pagamentos.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Nenhum pagamento encontrado.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      component="div"
                      count={pagamentos.length}
                      page={pagamentosPage}
                      onPageChange={(_e, p) => setPagamentosPage(p)}
                      rowsPerPage={pagamentosSize}
                      onRowsPerPageChange={(e) => {
                        setPagamentosSize(parseInt(e.target.value, 10))
                        setPagamentosPage(0)
                      }}
                      rowsPerPageOptions={[10, 20, 50]}
                      labelRowsPerPage="Linhas:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                  </SectionCard>
                )}

                {tab === 'coins' && (
                  <SectionCard
                    title="Extrato Warzone Coins"
                    subtitle={`Saldo: ${extrato?.saldo?.toLocaleString('pt-BR') ?? '—'} • Total adicionado: ${extrato?.totalAdicionado?.toLocaleString('pt-BR') ?? '—'} • Total gasto: ${extrato?.totalGasto?.toLocaleString('pt-BR') ?? '—'}`}
                    icon={<Paid sx={{ opacity: 0.85 }} />}
                  >
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell align="right">Qtd</TableCell>
                            <TableCell>Descrição</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {slicePage(coinsItems, coinsPage, coinsSize).map((it: any, idx: number) => (
                            <TableRow key={`${it?.createdAt ?? 'row'}-${idx}`}>
                              <TableCell>{fmtDateTime(it?.createdAt)}</TableCell>
                              <TableCell>{historicoTipoChip(it?.tipo)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                {typeof it?.quantidade === 'number' ? it.quantidade.toLocaleString('pt-BR') : '—'}
                              </TableCell>
                              <TableCell>{it?.descricao ?? '—'}</TableCell>
                            </TableRow>
                          ))}
                          {coinsItems.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Sem movimentações.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      component="div"
                      count={coinsItems.length}
                      page={coinsPage}
                      onPageChange={(_e, p) => setCoinsPage(p)}
                      rowsPerPage={coinsSize}
                      onRowsPerPageChange={(e) => {
                        setCoinsSize(parseInt(e.target.value, 10))
                        setCoinsPage(0)
                      }}
                      rowsPerPageOptions={[10, 15, 25, 50]}
                      labelRowsPerPage="Linhas:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                  </SectionCard>
                )}

                {tab === 'vip' && (
                  <SectionCard
                    title="Histórico VIP (Palworld)"
                    subtitle="Concessões/renovações registradas."
                    icon={<Star sx={{ opacity: 0.85 }} />}
                  >
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell align="right">Dias</TableCell>
                            <TableCell>Expira em</TableCell>
                            <TableCell>Motivo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {slicePage(vipHistorico, vipHistPage, vipHistSize).map((h: any, idx: number) => (
                            <TableRow key={h?.id ?? `${h?.createdAt ?? 'row'}-${idx}`}>
                              <TableCell>{fmtDateTime(h?.createdAt)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                {h?.diasVip ?? '—'}
                              </TableCell>
                              <TableCell>{fmtDateTime(h?.dataExpiracao)}</TableCell>
                              <TableCell>{h?.motivo ?? '—'}</TableCell>
                            </TableRow>
                          ))}
                          {vipHistorico.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Sem histórico de VIP.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      component="div"
                      count={vipHistorico.length}
                      page={vipHistPage}
                      onPageChange={(_e, p) => setVipHistPage(p)}
                      rowsPerPage={vipHistSize}
                      onRowsPerPageChange={(e) => {
                        setVipHistSize(parseInt(e.target.value, 10))
                        setVipHistPage(0)
                      }}
                      rowsPerPageOptions={[10, 20, 50]}
                      labelRowsPerPage="Linhas:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                  </SectionCard>
                )}

                {tab === 'supplies' && (
                  <SectionCard
                    title="Itens comprados"
                    subtitle="Itens pendentes/entregues no sistema."
                    icon={<ReceiptLong sx={{ opacity: 0.85 }} />}
                  >
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell align="right">Qtd</TableCell>
                            <TableCell align="center">Entregue</TableCell>
                            <TableCell>Player</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {slicePage(supplies, suppliesPage, suppliesSize).map((s: any, idx: number) => (
                            <TableRow key={`${s?.mongoId ?? s?.id ?? 'row'}-${idx}`}>
                              <TableCell>{s?.item ?? '—'}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                {typeof s?.count === 'number' ? s.count.toLocaleString('pt-BR') : '—'}
                              </TableCell>
                              <TableCell align="center">
                                {boolChip(typeof s?.delivered === 'boolean' ? s.delivered : undefined)}
                              </TableCell>
                              <TableCell>{s?.player ?? '—'}</TableCell>
                            </TableRow>
                          ))}
                          {supplies.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Nenhum supply encontrado.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      component="div"
                      count={supplies.length}
                      page={suppliesPage}
                      onPageChange={(_e, p) => setSuppliesPage(p)}
                      rowsPerPage={suppliesSize}
                      onRowsPerPageChange={(e) => {
                        setSuppliesSize(parseInt(e.target.value, 10))
                        setSuppliesPage(0)
                      }}
                      rowsPerPageOptions={[10, 20, 50]}
                      labelRowsPerPage="Linhas:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                  </SectionCard>
                )}

                {tab === 'grants' && (
                  <SectionCard
                    title="Itens concedidos pela administração"
                    subtitle="Registro de envios de itens para o Palworld."
                    icon={<ReceiptLong sx={{ opacity: 0.85 }} />}
                  >
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Player</TableCell>
                            <TableCell>Motivo</TableCell>
                            <TableCell align="right">Itens</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {slicePage(grants, grantsPage, grantsSize).map((g: any, idx: number) => (
                            <TableRow key={g?.id ?? `${g?.createdAt ?? 'row'}-${idx}`}>
                              <TableCell>{fmtDateTime(g?.createdAt)}</TableCell>
                              <TableCell>{g?.palPlayerName ?? '—'}</TableCell>
                              <TableCell>{g?.reason ?? '—'}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                {Array.isArray(g?.items) ? g.items.length.toLocaleString('pt-BR') : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                          {grants.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Nenhum item grant encontrado.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      component="div"
                      count={grants.length}
                      page={grantsPage}
                      onPageChange={(_e, p) => setGrantsPage(p)}
                      rowsPerPage={grantsSize}
                      onRowsPerPageChange={(e) => {
                        setGrantsSize(parseInt(e.target.value, 10))
                        setGrantsPage(0)
                      }}
                      rowsPerPageOptions={[10, 20, 50]}
                      labelRowsPerPage="Linhas:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                  </SectionCard>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Stack>
    </Container>
  )
}

export default AdminUsuarioCompletoPage
